
"use client";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Container, Typography, IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";

const FooterButton = styled("button")(({ theme }) => ({
  background: "#7C3AED",
  color: "#fff",
  border: "none",
  borderRadius: "50px",
  padding: "15px 22px",
  fontSize: "16px",
  fontWeight: 600,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: "6px",
  fontFamily: "var(--font-outfit)",
  whiteSpace: "nowrap",
  transition: "all 0.3s ease-in-out",

  [theme.breakpoints.down("md")]: {
    padding: "10px 16px",
    fontSize: "13px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "8px 12px",
    fontSize: "12px",
    gap: "4px",
  },

  "&:hover": {
    background: "#7C3AED",
    transform: "scale(1.05)",
  },
  "&:active": { transform: "translateY(0)" },
}));

const AdsFooter = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  const [isPinned, setIsPinned] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [isNearBottom, setIsNearBottom] = useState(false);
  const [footerHeight, setFooterHeight] = useState(0);

  const footerRef = useRef(null);

  // ── Measure footer height whenever it changes size (fonts loading, resize, RTL switch, etc.) ──
  useEffect(() => {
    const el = footerRef.current;
    if (!el) return;

    const updateHeight = () => {
      setFooterHeight(el.offsetHeight);
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(el);
    return () => resizeObserver.disconnect();
  }, []);

  // ── Visibility via IntersectionObserver ───────────────────────
  // IMPORTANT: We deliberately avoid scroll-event handlers that call
  // getBoundingClientRect()/scrollHeight. Reading layout geometry inside
  // a scroll/resize handler forces a *synchronous* layout recalculation
  // on every single frame (a.k.a. "layout thrashing"). Combined with other
  // effects on the page (image/font loads, other ResizeObservers, marquee
  // animations) this was causing the footer — and the scrollbar itself —
  // to visibly flicker in and out even when the user wasn't scrolling.
  //
  // IntersectionObserver is async, batched by the browser, and never
  // forces layout, so it's the correct tool for "has this point scrolled
  // into view" style checks.
  //
  // Trigger point: a sentinel element (#footer-trigger-sentinel) placed
  // inside TimeBreakdownSection, right after its heading/subtitle. The
  // footer appears once that sentinel scrolls into the bottom ~80% of the
  // viewport — i.e. once a good chunk of that section is already visible,
  // matching the "You Don't Have A Hiring Problem..." reference view.
  // Once true, we keep it true (monotonic) so the footer doesn't flicker
  // in/out if the user scrolls back up slightly.
  //
  // NOTE: the sentinel lives inside a <ScrollReveal> wrapped section, which
  // can mount it into the DOM *after* this effect's first run (lazy/animated
  // mount). So we must NOT assume "not found yet" means "show the footer" —
  // that was the previous bug (it made the footer visible from page load,
  // i.e. on sections 1 & 2, and — because visibility was monotonic — it
  // never hid again even when scrolling back up). Instead we wait for the
  // sentinel to actually appear in the DOM via a MutationObserver, then
  // attach the IntersectionObserver to it.
  useEffect(() => {
    let triggerObserver;
    let mutationObserver;

    const attachObserver = (triggerEl) => {
      triggerObserver = new IntersectionObserver(
        ([entry]) => {
          // entry.boundingClientRect is provided free by the observer
          // (no forced synchronous layout read), so this stays perf-safe.
          if (entry.isIntersecting) {
            // Sentinel is currently in view -> show.
            setIsVisible(true);
          } else if (entry.boundingClientRect.top < 0) {
            // Sentinel has scrolled ABOVE the viewport, i.e. user is at
            // section 3 or anywhere further down the page -> keep showing.
            setIsVisible(true);
          } else {
            // Sentinel is still BELOW the viewport, i.e. user hasn't
            // reached section 3 yet (sections 1 & 2) -> hide.
            setIsVisible(false);
          }
        },
        { threshold: 0, rootMargin: "0px 0px -20% 0px" },
      );
      triggerObserver.observe(triggerEl);
    };

    const existing = document.getElementById("footer-trigger-sentinel");
    if (existing) {
      attachObserver(existing);
      return () => triggerObserver?.disconnect();
    }

    // Sentinel not mounted yet — wait for it instead of defaulting to visible.
    mutationObserver = new MutationObserver(() => {
      const el = document.getElementById("footer-trigger-sentinel");
      if (el) {
        mutationObserver.disconnect();
        attachObserver(el);
      }
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      mutationObserver?.disconnect();
      triggerObserver?.disconnect();
    };
  }, []);

  // ── Near-bottom detection via a sentinel element ──────────────
  // Instead of comparing scrollY/scrollHeight numerically (which requires
  // reading layout on every scroll tick), we observe a 1px sentinel placed
  // near the end of the page. When it enters the viewport, we know we're
  // near the bottom.
  useEffect(() => {
    const sentinel = document.getElementById("ads-footer-bottom-sentinel");
    if (!sentinel) return;

    const bottomObserver = new IntersectionObserver(
      ([entry]) => setIsNearBottom(entry.isIntersecting),
      { threshold: 0, rootMargin: "200px 0px 0px 0px" },
    );

    bottomObserver.observe(sentinel);
    return () => bottomObserver.disconnect();
  }, []);

  const handleClose = useCallback(() => {
    setIsPinned(false);
  }, []);

  // Reserve space with a spacer instead of touching document.body directly.
  // This decouples footer visibility from the page's scrollHeight,
  // preventing the feedback loop that made the scrollbar appear/disappear.
  const showSpacer = isPinned && isVisible;

  return (
    <>
      {/* ── Sentinel: a zero-height marker at the natural end of the page.
          When this scrolls into view, we know we're near the bottom.
          Used instead of comparing scrollY/scrollHeight numbers. ── */}
      <Box
        id="ads-footer-bottom-sentinel"
        aria-hidden="true"
        sx={{ height: 0 }}
      />

      {/* ── Spacer: reserves layout space so content isn't hidden behind the fixed footer ── */}
      <Box
        aria-hidden="true"
        sx={{
          height: showSpacer ? `${footerHeight}px` : 0,
          transition: "height 0.3s ease",
        }}
      />

      <Box
        ref={footerRef}
        sx={{
          position: isPinned ? "fixed" : "static",
          bottom: isPinned ? 0 : "auto",
          left: isPinned ? 0 : "auto",
          right: isPinned ? 0 : "auto",
          zIndex: isPinned ? 1300 : "auto",
          width: "100%",
          background: "#201430",
          borderTop: "1px solid rgba(255,255,255,0.08)",
          direction: isRTL ? "rtl" : "ltr",
          opacity: isVisible ? 1 : 0,
          pointerEvents: isVisible ? "auto" : "none",
          transform: isVisible ? "translateY(0)" : "translateY(100%)",
          transition: "opacity 0.3s ease, transform 0.3s ease",
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexDirection: "row",
            py: { xs: 1, sm: 1.5 },
            minHeight: { xs: "56px", sm: "64px" },
            px: { xs: 1.5, sm: 2, md: 3 },
          }}
        >
          {/* ── LEFT TEXT ── */}
          <Box
            sx={{
              textAlign: isRTL ? "right" : "left",
              overflow: "hidden",
              mr: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                color: "#fff",
                fontWeight: 600,
                fontSize: { xs: "11px", sm: "13px", md: "16px" },
                fontFamily: "var(--font-outfit)",
                mb: "1%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: { xs: "nowrap", sm: "normal" },
              }}
            >
              {t("AdsFooterNew.tagline")}
            </Typography>
            {/* ── fix: subtext ab xs par bhi show hoga ── */}
            <Typography
              sx={{
                color: "rgba(255,255,255,0.55)",
                fontSize: { xs: "10px", sm: "11px", md: "13px" },
                fontFamily: "var(--font-outfit)",
              }}
            >
              {t("AdsFooterNew.subtext")}
            </Typography>
          </Box>

          {/* ── RIGHT: BUTTON + CLOSE ── */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 1.5 },
              flexShrink: 0,
            }}
          >
            <FooterButton
              id="btn-ten"
              // onClick={() => {
              //   const el = document.getElementById("book-demo-section");
              //   if (el) el.scrollIntoView({ behavior: "smooth" });
              // }}
              onClick={() => {
                window.dispatchEvent(new Event("open-book-demo-modal"));
              }}
            >
              {t("AdsFooterNew.bookDemo")}
              {isRTL ? (
                <ArrowBackIcon
                  sx={{ fontSize: { xs: 14, sm: 16, md: 18 }, mt: "2%" }}
                />
              ) : (
                <ArrowForwardIcon
                  sx={{ fontSize: { xs: 14, sm: 16, md: 18 } }}
                />
              )}
            </FooterButton>

            {isPinned && !isNearBottom && (
              <IconButton
                onClick={handleClose}
                sx={{
                  color: "rgba(255,255,255,0.5)",
                  border: "1.5px solid rgba(255,255,255,0.25)",
                  width: { xs: 26, sm: 32 },
                  height: { xs: 26, sm: 32 },
                  "&:hover": {
                    color: "#fff",
                    borderColor: "rgba(255,255,255,0.6)",
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: { xs: 13, sm: 16 } }} />
              </IconButton>
            )}
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default AdsFooter;